/**
 * Single-flight lock for the sync engine (Phase 16C C4): one tab syncs at a time.
 * Web Locks (`navigator.locks`) where available; otherwise a BroadcastChannel lease.
 * A lock function runs `fn` and resolves true, or resolves false at once when another holder has it.
 */
export type LockFn = (name: string, fn: () => Promise<void>) => Promise<boolean>;

interface LockManagerLike {
  request(name: string, options: { ifAvailable: boolean }, cb: (lock: unknown) => Promise<boolean>): Promise<boolean>;
}

const LEASE_MS = 60_000;
const held = new Set<string>();
const othersUntil = new Map<string, number>();
let channel: BroadcastChannel | null = null;

function leaseChannel(): BroadcastChannel | null {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  try {
    channel = new BroadcastChannel('pelora-sync-lease');
    channel.onmessage = (event: MessageEvent<{ name: string; until: number }>) => {
      if (event.data && typeof event.data.name === 'string') othersUntil.set(event.data.name, event.data.until);
    };
  } catch {
    channel = null;
  }
  return channel;
}

/** Fallback for browsers without Web Locks. */
export const leaseLock: LockFn = async (name, fn) => {
  const ch = leaseChannel();
  if (held.has(name) || (othersUntil.get(name) || 0) > Date.now()) return false;
  held.add(name);
  ch?.postMessage({ name, until: Date.now() + LEASE_MS });
  try {
    await fn();
  } finally {
    held.delete(name);
    ch?.postMessage({ name, until: 0 });
  }
  return true;
};

export const defaultLock: LockFn = async (name, fn) => {
  const locks = typeof navigator !== 'undefined' ? (navigator as Navigator & { locks?: LockManagerLike }).locks : undefined;
  if (locks?.request) {
    return locks.request(name, { ifAvailable: true }, async (lock) => {
      if (!lock) return false;
      await fn();
      return true;
    });
  }
  return leaseLock(name, fn);
};

/** In-memory lock shared by engines in one test (two "tabs"). */
export function createMemoryLock(): LockFn {
  const busy = new Set<string>();
  return async (name, fn) => {
    if (busy.has(name)) return false;
    busy.add(name);
    try {
      await fn();
    } finally {
      busy.delete(name);
    }
    return true;
  };
}
